'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { Button } from '@workspace/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@workspace/ui/components/command';
import { Input } from '@workspace/ui/components/input';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@workspace/ui/components/popover';
import { cn } from '@workspace/ui/lib';
import type { Country } from '@workspace/ui/types';
import { type CountryCode, getExampleNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

export interface PhoneInputSelectOption {
  value: string;
  id: string | number;
  iso_code?: string;
  countryName?: string;
}

interface PrefixSelectProps {
  id?: string;
  options: PhoneInputSelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  inModal?: boolean;
  size?: 'sm' | 'lg';
}

const defaultCountry = {
  iso_code: 'US',
  dial_code: '+1',
  placeholder: '(201) 555-0123',
};

interface PhoneNumberValue {
  id?: string;
  prefix: string;
  number: string;
  iso_code?: string;
  image?: {
    path: string;
    alt: string;
  };
}

export function getPhoneNumberSchema(countries: Country[]) {
  return z
    .object({
      id: z.string().optional(),
      prefix: z.string(),
      number: z.string(),
      iso_code: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (!data.number) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number is required',
        });
        return;
      }

      if (!/^\d+$/.test(data.number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Phone number must contain only digits',
        });
        return;
      }

      const country = countries.find((c) => c.dial_code === data.prefix);

      if (!country?.iso_code) return;

      const phoneNumber = parsePhoneNumberFromString(
        data.number,
        country.iso_code.toUpperCase() as CountryCode
      );

      if (!phoneNumber?.isValid()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid phone number',
        });
      }
    });
}

export function getOptionalPhoneNumberSchema(countries: Country[]) {
  return z.union([
    getPhoneNumberSchema(countries),
    z.object({
      id: z.string().optional(),
      prefix: z.string(),
      number: z.literal(''),
      iso_code: z.string().optional(),
    }),
  ]);
}

function getCountryPlaceholder(countryCode?: string) {
  if (!countryCode) return defaultCountry.placeholder;

  try {
    const example = getExampleNumber(
      countryCode.toUpperCase() as CountryCode,
      examples as Parameters<typeof getExampleNumber>[1]
    );

    if (!example) return defaultCountry.placeholder;
    const national = example.formatNational();

    // Remove leading zero if present and followed by a digit (e.g., "072 345 678" -> "72 345 678")
    const placeholder = national.replace(/^0(\d)/, '$1');

    return placeholder;
  } catch {
    return defaultCountry.placeholder;
  }
}

export function PrefixSelect({
  options,
  value,
  onChange,
  emptyMessage,
  searchPlaceholder,
  className,
  disabled = false,
  open = false,
  size = 'lg',
}: PrefixSelectProps) {
  const selectedItemRef = useRef<HTMLDivElement>(null);
  const commandListRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const isSmall = size === 'sm';

  const handleSelect = (currentValue: string) => {
    if (onChange) {
      onChange(currentValue);
    }
  };

  // Find the selected option for display - use iso_code as unique identifier
  const selectedOption = options.find((option) => option.iso_code === value);

  // Scroll to selected item when dropdown opens (but not when searching)
  useEffect(() => {
    if (open && !hasSearched) {
      let retryCount = 0;
      const maxRetries = 10;

      // Wait for elements to be rendered
      const checkAndScroll = () => {
        if (selectedItemRef.current && commandListRef.current) {
          const selectedElement = selectedItemRef.current;
          const scrollContainer = commandListRef.current;

          // Calculate the position to scroll to
          const itemTop = selectedElement.offsetTop;
          const containerHeight = scrollContainer.clientHeight;
          const itemHeight = selectedElement.clientHeight;

          // Center the item in the container
          const scrollTop = itemTop - containerHeight / 2 + itemHeight / 2;

          scrollContainer.scrollTop = scrollTop;
        } else if (retryCount < maxRetries) {
          // Retry after a short delay
          retryCount++;
          setTimeout(checkAndScroll, 50);
        }
      };

      // Start checking after a delay to allow rendering
      setTimeout(checkAndScroll, 100);
    }
  }, [open, hasSearched]);

  // Reset search state when dropdown closes
  useEffect(() => {
    if (!open) {
      setHasSearched(false);
      setSearchValue('');
    }
  }, [open]);

  // Handle search input change
  const handleSearchChange = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
    if (newSearchValue) {
      if (!hasSearched) {
        setHasSearched(true);
      }
      // Always scroll to top when search value changes
      if (commandListRef.current) {
        commandListRef.current.scrollTop = 0;
      }
    }
  };

  return (
    <>
      <PopoverTrigger asChild>
        <Button
          variant="input"
          size="input"
          role="combobox"
          translate="no"
          disabled={disabled}
          className={cn(
            className,
            'notranslate disabled:text-grey-300 dark:disabled:text-grey-900 h-full w-[125px] min-w-[120px] border-none text-neutral-200 focus:outline-0 active:outline-0 dark:text-neutral-100',
            isSmall ? 'min-h-[36px]' : 'min-h-[48px]'
          )}
        >
          <div className={cn('flex items-center gap-2 align-middle text-sm font-normal')}>
            <Image
              src={`https://i.nativeteams.com/assets/app/flags_code/${
                selectedOption?.iso_code ?? defaultCountry.iso_code
              }.svg`}
              alt={selectedOption?.iso_code ?? defaultCountry.iso_code}
              width={24}
              height={24}
              priority
            />
            {selectedOption?.value || defaultCountry.dial_code}
          </div>
          <ChevronDown className="group-disabled:text-grey-300 dark:group-disabled:text-grey-900 h-4 w-4 text-neutral-200 group-data-[state=open]:rotate-180 dark:text-neutral-100" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        sideOffset={6}
        align="start"
        translate="no"
        className={cn(
          'notranslate w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] overflow-y-scroll p-4'
        )}
      >
        <Command>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList ref={commandListRef} className="h-[275px] w-full overflow-y-scroll">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup className="flex flex-col px-3 py-4">
              {options.map((option, index) => {
                const isSelected = option.iso_code
                  ? option.iso_code === value
                  : option.value === value;
                return (
                  <CommandItem
                    key={`${option.value}-${option.id}`}
                    value={`${option.value}-${option.countryName}`}
                    onSelect={() => handleSelect(option.iso_code || option.value)}
                    ref={isSelected ? selectedItemRef : undefined}
                    className={cn(
                      'mr-2 flex h-12 justify-between gap-2 rounded-2xl pr-3',
                      'hover:bg-grey-25 dark:hover:bg-grey-900 dark:hover:text-grey-400 cursor-pointer hover:font-medium hover:text-neutral-200',
                      index !== options.length - 1 && 'mb-5',
                      isSelected &&
                        'bg-grey-25 dark:bg-grey-900 dark:text-grey-400 font-medium text-neutral-200'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Image
                        src={`https://i.nativeteams.com/assets/app/flags_code/${(
                          option.iso_code ?? defaultCountry.iso_code
                        ).toUpperCase()}.svg`}
                        alt={option.iso_code || defaultCountry.iso_code}
                        width={24}
                        height={24}
                      />
                      {option.value}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </>
  );
}

type PhoneNumberSelectProps = {
  name?: string;
  value: PhoneNumberValue;
  onChange: (value: PhoneNumberValue, country?: Country) => void;
  onBlur: () => void;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  countries: Country[];
  inModal?: boolean;
  readOnly?: boolean;
  size?: 'sm' | 'lg';
};

export const PhoneNumberSelect = ({
  value,
  onChange,
  disabled,
  name,
  onBlur,
  countries,
  readOnly = false,
  inModal = false,
  size = 'lg',
  ...props
}: PhoneNumberSelectProps & {
  'aria-invalid'?: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const selectedCountry = useMemo(
    () => countries?.find((country) => country.iso_code === value.iso_code),
    [value.iso_code, countries]
  );

  // Get the correct ISO code for the current selection
  const currentIsoCode = useMemo(() => {
    // If we have an ISO code in the value, use that (highest priority)
    if (value.iso_code) {
      return value.iso_code;
    }
    // If we have a selectedCountry, use its ISO code
    if (selectedCountry?.iso_code) {
      return selectedCountry.iso_code;
    }
    // Fallback to the first country with this dial code
    const countryWithDialCode = countries?.find((c) => c.dial_code === value.prefix);
    return countryWithDialCode?.iso_code || value.prefix;
  }, [selectedCountry, value.iso_code, value.prefix, countries]);

  // Memoize prefix options to avoid recreating on every render
  const prefixOptions = useMemo(() => {
    return countries.map((country) => ({
      id: country.id || country.iso_code,
      value: country.dial_code,
      iso_code: country.iso_code,
      countryName: country.name,
    }));
  }, [countries]);

  // Memoize the onChange handler to prevent unnecessary re-renders
  const handlePrefixChange = useCallback(
    (selectedValue: string) => {
      // selectedValue is now the ISO code, find the country by ISO code
      const selectedCountry = countries.find((c) => c.iso_code === selectedValue);
      onChange(
        {
          id: selectedCountry?.id?.toString() || undefined,
          prefix: selectedCountry?.dial_code || selectedValue,
          number: value.number,
          iso_code: selectedCountry?.iso_code,
          image: {
            path: `https://i.nativeteams.com/assets/app/flags_code/${selectedCountry?.iso_code.toUpperCase()}.svg`,
            alt: selectedCountry?.name || '',
          },
        },
        selectedCountry
      );
      setOpen(false);
    },
    [countries, onChange, value.number]
  );

  // Memoize the input change handler
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/\D/g, '');
      // Preserve the current ISO code instead of finding by dial code
      const selectedCountry =
        countries.find((c) => c.iso_code === value.iso_code) ||
        countries.find((c) => c.dial_code === value.prefix);
      onChange(
        {
          id: value.id,
          prefix: value.prefix,
          number: inputValue,
          iso_code: value.iso_code || selectedCountry?.iso_code,
          image: {
            path: `https://i.nativeteams.com/assets/app/flags_code/${(
              value.iso_code || selectedCountry?.iso_code || 'US'
            ).toUpperCase()}.svg`,
            alt: selectedCountry?.name || '',
          },
        },
        selectedCountry
      );
    },
    [countries, onChange, value.id, value.prefix, value.iso_code]
  );

  const isSmall = size === 'sm';

  return readOnly ? (
    <input
      data-slot="input"
      className={cn(
        'peer [&:not(:placeholder-shown):not(:disabled)]:text-primary placeholder:text-grey-700 dark:placeholder:text-grey-500 dark:disabled:placeholder:text-grey-900 dark:disabled:text-grey-900 file:text-foreground selection:bg-grey-100 dark:text-grey-700 selection:text-primary-foreground disabled:border-grey-100 dark:disabled:border-grey-900 disabled:placeholder:text-grey-300 disabled:text-grey-300 disabled:outline-grey-100 aria-invalid:border-destructive dark:[&:not(:placeholder-shown)]:not(:disabled)]:text-neutral-100 flex w-full min-w-0 border bg-transparent shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:outline-1 disabled:outline-offset-[-1px] dark:hover:border-neutral-100 dark:hover:text-neutral-100 dark:aria-invalid:border-red-400 dark:[&:not(:placeholder-shown):not(:disabled)]:text-white',
        size === 'sm'
          ? 'h-9 rounded-xl px-3 py-2.5 text-sm'
          : 'h-12 rounded-2xl px-4 py-3 text-base',
        readOnly
          ? 'text-grey-600! border-grey-300 outline-grey-300 hover:border-grey-300 focus-visible:border-grey-300 active:border-grey-300 dark:border-grey-700 pointer-events-none cursor-default shadow-none outline-1 outline-offset-[-1px]'
          : 'border-grey-300 hover:border-primary focus-visible:border-primary dark:border-grey-700'
      )}
      readOnly={true}
      value={`${value.prefix.startsWith('+') ? value.prefix : `+${value.prefix}`} ${value.number}`}
    />
  ) : (
    <Popover open={open} onOpenChange={setOpen} modal={inModal}>
      <PopoverAnchor asChild>
        <div
          role="group"
          aria-labelledby={name}
          className={cn(
            'border-grey-300 dark:border-grey-500 selected:outline-0 relative box-border flex w-full border focus-within:border-neutral-200 focus:outline-none dark:focus-within:border-neutral-100',
            isSmall ? 'h-9 rounded-xl' : 'h-[48px] rounded-2xl',
            open && 'border-neutral-200 dark:border-neutral-100',
            props['aria-invalid'] &&
              'border-red-500 focus-within:border-red-500 hover:border-red-500 dark:border-red-400 dark:focus-within:border-red-400 dark:hover:border-red-400'
          )}
        >
          <PrefixSelect
            open={open}
            size={size}
            value={currentIsoCode}
            onChange={handlePrefixChange}
            options={prefixOptions}
            disabled={disabled}
          />

          <Input
            disabled={disabled}
            value={value.number}
            maxLength={15}
            onBlur={onBlur}
            onChange={handleInputChange}
            placeholder={getCountryPlaceholder(selectedCountry?.iso_code)}
            className={cn(
              '!border-l-grey-300 dark:border-l-grey-400 hover:text-grey-700 disabled:placeholder:text-grey-300 dark:disabled:placeholder:text-grey-900 z-10 h-full w-full min-w-0 flex-1 rounded-none border-t-0 border-r-0 border-b-0 !border-l leading-tight shadow-none !ring-0 !outline-none dark:hover:text-neutral-100',
              isSmall ? 'py-2.5 pr-3 pl-3 text-sm' : 'py-2 pr-4 pl-3 text-base',
              disabled && '!text-grey-300 !dark:text-grey-900'
            )}
          />
        </div>
      </PopoverAnchor>
    </Popover>
  );
};
